import { useState } from "react";
import { Button } from "./ui/Button";
import { Badge, bucketVariant, bucketLabel } from "./ui/Badge";
import { Tooltip } from "./ui/Tooltip";
import { Dialog, DialogTitle, DialogDescription } from "./ui/Dialog";
import CandidatePicker from "./CandidatePicker";
import ChainPicker from "./ChainPicker";
import type { BucketRow } from "./BucketList";
import { deriveSignals, isWeakMatch, type Signal } from "../lib/score-signal-labels";
import { displayLicenseName, normalizeDbaName } from "../lib/display-name";

interface Props {
  row: BucketRow;
  onConfirm: (row: BucketRow, payload: { link_status: string; license_number?: string | null; link_method?: string; notes?: string }) => Promise<void>;
  onNotARetailer: (row: BucketRow) => Promise<void>;
  onEnrichStart: (license: string) => void;
  onMarkDeliveryPartner: (row: BucketRow) => void;
  onLinkChain: (row: BucketRow, chainId: string, chainDisplayName: string) => Promise<void>;
}

function parseReasons(row: BucketRow): string[] {
  const out: string[] = [];
  if (row.notes) out.push(row.notes);

  const method = row.link_method;
  if (method) {
    if (method === "license_exact") out.push("The CM license number exactly matches a license in our California graph.");
    else if (method === "license_exact_unverified") out.push("License number matches, but we don't yet have a verified name or website on file — please supply one so we can confirm.");
    else if (method === "address_line1+city") out.push("Street address and city match a known license.");
    else if (method === "website_domain") out.push("Website domain matches a known license.");
    else if (method === "name+city") out.push("Name and city pair match a known license.");
    else if (method === "fuzzy_name") out.push("Fuzzy name similarity matched a nearby license — please confirm.");
    else if (method === "distributor_bridge") out.push("CM row is a C11 distributor license. We looked up the operating entity and found the associated retail license(s).");
    else if (method === "manual") out.push("Manually picked by a reviewer.");
  }

  return out;
}

const STRENGTH_BADGE: Record<Signal["level"], string> = {
  strong: "bg-[#F1F8F2] text-[#1C5125] border-[#BFE0C4]",
  medium: "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]",
  weak: "bg-gray-100 text-gray-500 border-gray-200",
};

function SignalTable({ signals }: { signals: Signal[] }) {
  return (
    <table className="mt-2 w-full text-xs" role="table" aria-label="Match signals">
      <thead>
        <tr className="text-left text-gray-400">
          <th className="font-medium py-1 pr-3">Signal</th>
          <th className="font-medium py-1 pr-3">Strength</th>
        </tr>
      </thead>
      <tbody>
        {signals.map((s) => (
          <tr key={s.key} className="border-t border-gray-100" data-testid="signal-row" data-signal-key={s.key}>
            <td className="py-1 pr-3 text-gray-700">{s.label}</td>
            <td className="py-1 pr-3">
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${STRENGTH_BADGE[s.level]}`}
                data-signal-level={s.level}
              >
                {s.display}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Kv({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[92px_1fr] gap-2 py-1">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900 font-medium break-words">{children || <span className="text-gray-400 font-normal">—</span>}</dd>
    </div>
  );
}

export default function ReviewCard({ row, onConfirm, onNotARetailer, onEnrichStart, onMarkDeliveryPartner, onLinkChain }: Props) {
  const [picking, setPicking] = useState(false);
  const [linkingChain, setLinkingChain] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmingNotRetailer, setConfirmingNotRetailer] = useState(false);

  const variant = bucketVariant(row.link_status, row.license_number, row.user_confirmed);
  const label = bucketLabel(row.link_status, row.license_number, row.user_confirmed);
  const hasMatch = !!row.license_number;
  const reasons = parseReasons(row);
  const reasoningOpen = variant === "review"; // open by default for review
  const signals = deriveSignals(row.score_components);
  const hasSignals = signals.length > 0;
  const weakMatch = hasSignals && isWeakMatch(signals);

  const act = async (payload: Parameters<Props["onConfirm"]>[1]) => {
    setBusy(true);
    try { await onConfirm(row, payload); } finally { setBusy(false); }
  };

  const runNotARetailer = async () => {
    setBusy(true);
    try {
      await onNotARetailer(row);
    } finally {
      setBusy(false);
      setConfirmingNotRetailer(false);
    }
  };

  return (
    <article className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 text-sm">
          {row.link_method && (
            <span className="font-medium text-gray-700">
              {row.link_method}
              {row.confidence != null && <span className="text-gray-400"> · conf {Number(row.confidence).toFixed(2)}</span>}
            </span>
          )}
        </div>
        <Badge variant={variant}>{label}</Badge>
      </header>

      {weakMatch && (
        <div
          role="alert"
          data-testid="weak-match-warning"
          className="mb-4 rounded-md border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm font-medium text-[#991B1B]"
        >
          ⚠ Weak match — strongest signal is below 0.5. Review carefully.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="rounded-md border border-gray-200 bg-gray-50/60 p-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Your Customer Master</h4>
          <dl>
            <Kv label="License">{row.cm_license}</Kv>
            <Kv label="Name">{row.name}</Kv>
            {row.parent_name && row.parent_name !== row.name && <Kv label="Parent">{row.parent_name}</Kv>}
            <Kv label="City">{row.city}</Kv>
            <Kv label="Website">{row.website}</Kv>
            <Kv label="Territory">{row.territory}</Kv>
            {row.brand_supplied_name && (
              <Kv label="Brand-supplied">
                <span className="text-accent-700">{row.brand_supplied_name}</span>
              </Kv>
            )}
          </dl>
        </section>

        <section className={`rounded-md border p-4 ${hasMatch ? "border-gray-200 bg-white" : "border-dashed border-gray-300 bg-gray-50/40"}`}>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            {hasMatch ? "What we think it matches" : "No candidate match"}
          </h4>
          {hasMatch ? (
            <dl>
              <Kv label="License">{row.license_number}</Kv>
              <Kv label="Name">{displayLicenseName(row.db_display_name, row.dba_name, row.legal_name)}</Kv>
              {(() => {
                const dba = normalizeDbaName(row.dba_name);
                const primary = displayLicenseName(row.db_display_name, row.dba_name, row.legal_name);
                return dba && dba !== primary ? <Kv label="DBA">{dba}</Kv> : null;
              })()}
              <Kv label="City">{row.db_city}</Kv>
              <Kv label="Address">{row.db_street}</Kv>
            </dl>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <p className="text-sm text-gray-700 mb-1">No candidate match</p>
              <p className="text-xs text-gray-500 max-w-[260px]">
                We checked license number, address, website, name+city, and fuzzy name matching.
              </p>
            </div>
          )}
        </section>
      </div>

      {(hasSignals || reasons.length > 0) && (
        <details className="mt-3 group" open={reasoningOpen}>
          <summary className="cursor-pointer list-none inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
            <span className="inline-block transition-transform group-open:rotate-90">▸</span>
            <span>Why we think this is a match</span>
            {!hasSignals && <span className="text-gray-400">(legacy format)</span>}
          </summary>
          {hasSignals ? (
            <SignalTable signals={signals} />
          ) : (
            <ul className="mt-2 ml-4 list-disc text-xs text-gray-600 space-y-1">
              {reasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          )}
        </details>
      )}

      {row.chain_id && row.chain_display_name && (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-accent-50 px-2.5 py-1 text-xs font-medium text-accent-700 border border-accent-100">
          Already linked to: {row.chain_display_name}
          {row.chain_member_count != null && (
            <span className="text-accent-600">
              ({row.chain_member_count} {row.chain_member_count === 1 ? "store" : "stores"})
            </span>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {row.link_method === "license_exact_unverified" && row.license_number && (
          <Tooltip content="Supply a verified website or name">
            <Button variant="primary" size="sm" type="button" onClick={() => onEnrichStart(row.license_number!)} disabled={busy}>
              Supply website / name
            </Button>
          </Tooltip>
        )}
        {row.link_status !== "confirmed" && row.license_number && (
          <Tooltip content="Confirm match">
            <Button
              variant="primary"
              size="sm"
              type="button"
              onClick={() => act({ link_status: "confirmed", license_number: row.license_number, link_method: row.link_method ?? "manual" })}
              disabled={busy}
            >
              Confirm match
            </Button>
          </Tooltip>
        )}
        {row.link_status === "confirmed" && (
          <Tooltip content="Undo confirm and return to review">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => act({ link_status: "suggested", license_number: row.license_number, link_method: row.link_method ?? "manual" })}
              disabled={busy}
            >
              Undo confirm
            </Button>
          </Tooltip>
        )}
        <Tooltip content="Pick a different retailer from the database">
          <Button variant="secondary" size="sm" type="button" onClick={() => setPicking((p) => !p)} disabled={busy}>
            {picking ? "Close search" : "Pick different retailer ▾"}
          </Button>
        </Tooltip>
        <Tooltip content="Link this license to a retail chain (overrides the single-store guess)">
          <Button variant="secondary" size="sm" type="button" onClick={() => setLinkingChain((p) => !p)} disabled={busy}>
            {linkingChain ? "Close chains" : "Link to chain ▾"}
          </Button>
        </Tooltip>
        <Tooltip content="Delivery partner (service area, no street address)">
          <Button variant="secondary" size="sm" type="button" onClick={() => onMarkDeliveryPartner(row)} disabled={busy}>
            Mark as Delivery Partner
          </Button>
        </Tooltip>
        <Tooltip content="Not a retailer (broker, sponsor, test row)">
          <Button
            variant="danger-ghost"
            size="sm"
            type="button"
            onClick={() => setConfirmingNotRetailer(true)}
            disabled={busy}
          >
            Not a retailer
          </Button>
        </Tooltip>
        <Tooltip content="Skip (leave in review queue)">
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => act({ link_status: "rejected", link_method: "manual" })}
            disabled={busy}
          >
            Skip
          </Button>
        </Tooltip>
      </div>

      {linkingChain && (
        <div className="mt-4">
          <ChainPicker onLink={async (chainId, chainDisplayName) => {
            setBusy(true);
            try {
              await onLinkChain(row, chainId, chainDisplayName);
              setLinkingChain(false);
            } finally {
              setBusy(false);
            }
          }} />
        </div>
      )}

      {picking && (
        <div className="mt-4">
          <CandidatePicker onPick={async (lic) => {
            await act({ link_status: "confirmed", license_number: lic, link_method: "manual", notes: "manually picked" });
            setPicking(false);
          }} />
        </div>
      )}

      <Dialog open={confirmingNotRetailer} onOpenChange={(v) => !busy && setConfirmingNotRetailer(v)}>
        <DialogTitle>Mark as not a retailer?</DialogTitle>
        <DialogDescription>
          Mark this license as not a retailer? This will be recorded in the audit log. You can undo from the toast immediately after.
        </DialogDescription>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={() => setConfirmingNotRetailer(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" type="button" onClick={runNotARetailer} disabled={busy}>
            {busy ? "Marking…" : "Confirm"}
          </Button>
        </div>
      </Dialog>
    </article>
  );
}
